
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Congregation } from "./types";
import { useSession } from "@supabase/auth-helpers-react";

export function useCongregations() {
  const { toast } = useToast();
  const session = useSession();
  const [congregations, setCongregations] = useState<Congregation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Add network status listener
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchCongregations = async () => {
    if (isOffline) {
      setLoading(false);
      return [];
    }
    
    // Don't attempt to fetch too many times in a row
    if (retryCount > 3) {
      setLoading(false);
      setError(new Error("Too many failed attempts. Please try again later."));
      return [];
    }

    setLoading(true);
    setError(null);
    
    try {
      if (!session?.user?.id) {
        setCongregations([]);
        setLoading(false);
        return [];
      }
      
      // First check for user's congregation from profile
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("congregation_name")
        .eq("id", session.user.id)
        .maybeSingle();
        
      if (profileError) {
        console.error("Error fetching profile:", profileError);
        throw profileError;
      }
      
      // If profile has congregation name, fetch that specific congregation
      if (profile?.congregation_name) {
        const { data: congregationsData, error: congregationsError } = await supabase
          .from("congregations")
          .select("*")
          .eq("name", profile.congregation_name);

        if (congregationsError) throw congregationsError;
        setCongregations(congregationsData || []);
        setRetryCount(0); // Reset retry count on success
        setLoading(false);
        return congregationsData || [];
      }
      
      // Otherwise, fetch congregations created by this user
      const { data: congregationsData, error: congregationsError } = await supabase
        .from("congregations")
        .select("*")
        .eq("created_by", session.user.id)
        .order("name");

      if (congregationsError) throw congregationsError;
      setCongregations(congregationsData || []);
      setRetryCount(0); // Reset retry count on success
      setLoading(false);
      return congregationsData || [];
    } catch (error: any) {
      console.error("Error fetching congregations:", error);
      
      // Increment retry count
      setRetryCount(prev => prev + 1);
      
      // Set error state
      setError(error instanceof Error ? error : new Error(String(error)));
      
      // Only show toast for the first error to avoid spamming
      if (retryCount === 0) {
        toast({
          title: "Connection Error",
          description: "Failed to load congregations. Please check your connection.",
          variant: "destructive",
        });
      }
      
      setLoading(false);
      return [];
    }
  };

  return {
    congregations,
    setCongregations,
    fetchCongregations,
    loading,
    setLoading,
    error,
    isOffline
  };
}
